import { useState, useEffect } from "react";
import type { PluginDetailProps, MarketplaceEntry } from "../types/index";
import styles from "./PluginDetailPage.module.css";
import InstallInstructions from "../components/InstallInstructions";
import InstallInClaude from "../components/InstallInClaude";
import DesktopDownload from "../components/DesktopDownload";
import VoteSection from "../components/VoteSection";
import { CLIENT_CONFIG } from "../components/ClientBadge";
import { OfficialBadge } from "../components/OfficialBadge";
import NewBadge from "../components/NewBadge";


import { deleteSkill, getPresignedDownloadUrl, GitHubProxyError, legacyInstallKey } from "../lib/github-proxy-client";
import { useAuth } from "../lib/auth-context";
import { isContributedRecord } from "../lib/contributed";
import { formatDataThroughDate } from "../lib/adoption";
import SharedUsageNote from "../components/SharedUsageNote";
import { env } from "../lib/env";

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export default function PluginDetailPage({ plugin, breadcrumbs = [], parentPlugin, dataThroughDate, onBackToCatalog, onBreadcrumbClick, onNavigateToPlugin, findPlugin, user, onEdit, onDelete, onClientClick, onCategoryClick, onKeywordClick, onHelp }: PluginDetailProps) {
  // Truthy check, not `!== undefined`: a 0 must render the empty state rather than "0" —
  // Contract 7 defines exactly one empty state and a measured zero never arrives.
  const activeUsers = plugin.adoption?.activeUsers30d;
  // "" when absent or unparseable, so the caption is simply omitted.
  const usageAsOf = dataThroughDate ? formatDataThroughDate(dataThroughDate) : "";
  // installKey routes the user and identifies the plugin in the URL — uses the
  // marketplace's installName (= manifestName post #176).
  const installKey = plugin.installName ?? legacyInstallKey(plugin);
  // proxyKey is the legacy-format install key that github-proxy's parseInstallKey
  // decodes into source/org/repo for download/delete operations.
  const proxyKey = legacyInstallKey(plugin);

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [downloadingSkill, setDownloadingSkill] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<{ skill: string; message: string } | null>(null);
  const { isAuthenticated: authenticated } = useAuth();

  useEffect(() => {
    setDownloadingSkill(null);
    setDownloadError(null);
  }, [installKey]);

  // Edit/Delete are gated on BOTH a contributor-email match AND the record actually
  // living in the contributed-skills write-path repo (issue #404). The proxy can only
  // write to that repo, so offering the affordance on a foreign (webhook-discovered)
  // record — even one whose contributorEmail happens to match — would be a dead or
  // wrong-target action. The server enforces the same gate; this just hides the UI.
  const isContributor =
    !!user?.email &&
    !!plugin.contributorEmail &&
    plugin.contributorEmail.toLowerCase() === user.email.toLowerCase() &&
    isContributedRecord(plugin);

  async function handleDelete() {
    if (!confirm("Are you sure you want to delete this? This cannot be undone.")) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteSkill(proxyKey);
      setDeleting(false);
      onDelete?.();
    } catch (err) {
      setDeleteError(err instanceof GitHubProxyError ? err.message : "Delete failed");
      setDeleting(false);
    }
  }

  // Hooks and sub-agents are the only components that don't run in Claude Chat —
  // they need the full plugin runtime (Claude Cowork and Claude Code). Key the
  // caveat on the hooks/agents component arrays the page already renders. This
  // mirrors the catalog's supportedClients chat-exclusion rule (which also keys
  // on hooks/sub-agents) but does not read from it, so the two could drift if
  // that upstream rule ever changes.
  const coworkOnlyFeatures = [
    (plugin.hooks?.length ?? 0) > 0 ? "hooks" : null,
    (plugin.agents?.length ?? 0) > 0 ? "sub-agents" : null,
  ].filter((f): f is string => f !== null);
  const coworkOnlyFeatureText =
    coworkOnlyFeatures.length === 2
      ? `${coworkOnlyFeatures[0]} and ${coworkOnlyFeatures[1]}`
      : coworkOnlyFeatures[0];


  return (
    <div>
      <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
        <button onClick={onBackToCatalog} className={styles.linkButton}>
          Marketplace
        </button>
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.installName ?? i} className={styles.breadcrumbCrumb}>
            <span className={styles.breadcrumbSep}>›</span>
            <button onClick={() => onBreadcrumbClick?.(crumb)} className={styles.linkButton}>
              {crumb.pluginName}
            </button>
          </span>
        ))}
        <span className={styles.breadcrumbSep}>›</span>
        <strong className={styles.breadcrumbCurrent}>{plugin.pluginName}</strong>
      </nav>

      <div className={styles.layout}>

        <div className={styles.mainCol}>
          <div className={styles.titleBlock}>
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{plugin.pluginName}</h1>
              <OfficialBadge plugin={plugin} />
              {plugin.extensionType && (
                <span className={`${styles.typeBadge} ${plugin.extensionType === "plugin" ? styles.typeBadgePlugin : styles.typeBadgeSkill}`}>
                  {plugin.extensionType}
                </span>
              )}
              <NewBadge plugin={plugin} />
            </div>
            <div className={styles.repoMeta}>
              <div>{plugin.orgOrOwner}/{plugin.repoName}</div>
              <div>{plugin.source === "ghec" ? "GHEC" : "GHES"}</div>
            </div>
          </div>

          <p className={styles.description}>
            {plugin.description}
          </p>

          {plugin.contributorName && (
            <p className={styles.contributor}>
              Contributed by <strong>{plugin.contributorName}</strong> on{" "}
              {new Date(plugin.firstDiscoveredAt).toLocaleDateString()}
            </p>
          )}

          {isContributor && (
            <div className={styles.actions}>
              <button type="button" onClick={onEdit} className={styles.editButton}>
                Edit
              </button>
              <button type="button" onClick={handleDelete} disabled={deleting} className={styles.deleteButton}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
              {deleteError && (
                <span className={styles.actionError}>
                  {deleteError}
                </span>
              )}
            </div>
          )}

          <div className={styles.metaGrid}>
            <span className={styles.metaLabel}>Version</span>
            <span className={styles.metaValue}>{plugin.version}</span>

            {plugin.author && <>
              <span className={styles.metaLabel}>Author</span>
              <span className={styles.metaValue}>{plugin.author}</span>
            </>}

            {plugin.license && <>
              <span className={styles.metaLabel}>License</span>
              <span className={styles.metaValue}>{plugin.license}</span>
            </>}

            <span className={styles.metaLabel}>Updated</span>
            <span className={styles.metaValue}>{new Date(plugin.lastUpdatedAt).toLocaleDateString()}</span>

            {parentPlugin && <>
              <span className={styles.metaLabel}>Plugin</span>
              <span>
                <button
                  type="button"
                  // Navigate UP to the parent plugin, same as clicking its breadcrumb
                  // crumb — not onNavigateToPlugin, which is forward navigation into a
                  // linked skill/plugin. The trail is derived from the route, so this is
                  // just a navigation.
                  onClick={() => onBreadcrumbClick?.(parentPlugin)}
                  className={styles.metaLink}
                >
                  {parentPlugin.pluginName} &rarr;
                </button>
              </span>
            </>}

            <span className={styles.metaLabel}>Repository</span>
            <span>
              {isSafeUrl(plugin.repoUrl) ? (
                <a href={plugin.repoUrl} target="_blank" rel="noopener noreferrer" className={styles.metaUrlLink}>
                  {plugin.repoUrl} &rarr;
                </a>
              ) : (
                <span className={styles.metaValue}>{plugin.repoUrl}</span>
              )}
            </span>

            {plugin.homepage && plugin.homepage !== plugin.repoUrl && isSafeUrl(plugin.homepage) && <>
              <span className={styles.metaLabel}>Homepage</span>
              <span>
                <a href={plugin.homepage} target="_blank" rel="noopener noreferrer" className={styles.metaUrlLink}>
                  {plugin.homepage} &rarr;
                </a>
              </span>
            </>}
          </div>

          {/* Standalone adoption callout AFTER the meta grid — matching the prototype's
              `adoptionSummaryBlock` (poc-site/public/pocs/ai-marketplace/index.html), which
              places it between the grid and the category/client chips. The stale plan doc
              specified a label/value row INSIDE the grid instead.
              `downloads` and the stickiness percentage the prototype also shows here are
              explicitly out of scope per the epic, so they are omitted. */}
          <div className={styles.adoptionBlock}>
            {activeUsers ? (
              <>
                {/* #266 AC 9 — the window lags "now" by ~3 days, so surface the as-of date
                    inline with the figure, pushed to the right and visually set apart. */}
                <div className={styles.adoptionHeader}>
                  <div>
                    <strong className={styles.adoptionFigure}>{activeUsers}</strong> active users{" "}
                    <span className={styles.adoptionWindow}>· last 30 days</span>
                  </div>
                  {usageAsOf && <span className={styles.adoptionAsOf}>as of {usageAsOf}</span>}
                </div>
                <SharedUsageNote sharedNameUsage={plugin.adoption?.sharedNameUsage} variant="long" />
              </>
            ) : (
              /* "no usage yet", matching the card and the list row exactly. #553 shipped the
                 prototype's "No usage data yet" here and flagged the divergence rather than
                 resolving it unilaterally; #620 settles it by quoting the string as the
                 required copy ("it shows the text 'no usage yet'"), so all three surfaces now
                 word the one empty state identically. */
              <div className={styles.adoptionEmpty}>no usage yet</div>
            )}
          </div>

          {/*
            Community feedback (#617/#618), in the main column right after the
            metadata grid and the adoption callout — the prototype's placement,
            immediately following its adoption summary block and before
            Category / Works with. That order is now real rather than anticipated:
            #553 merged, so the callout above is the block this was always meant to
            follow.

            An earlier revision put this in the right-hand aside below the install
            actions, reasoning that it must not outrank the primary CTA. That was an
            invention: the validated design puts it in the main content flow, where
            someone reading about a skill meets the question in sequence rather than
            having to notice a sidebar.

            Keyed on installKey so switching entries resets its vote state rather than
            briefly showing the previous entry's vote.
          */}
          <VoteSection key={`vote-${installKey}`} plugin={plugin} />

          <div className={styles.chipSections}>
            {plugin.categories && plugin.categories.length > 0 && (
              <div>
                <div className={styles.sectionLabel}>Category</div>
                <div className={styles.chipRow}>
                  {plugin.categories.map((category) =>
                    onCategoryClick ? (
                      <button
                        key={category}
                        type="button"
                        onClick={() => onCategoryClick(category)}
                        className={styles.chipClickable}
                      >
                        {category}
                      </button>
                    ) : (
                      <span key={category} className={styles.chip}>
                        {category}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}

            {plugin.supportedClients && plugin.supportedClients.length > 0 && (
              <div>
                <div className={styles.sectionLabel}>Works with</div>
                <div className={styles.chipRow}>
                  {plugin.supportedClients.map((client) => {
                    const cfg = CLIENT_CONFIG[client] ?? { label: client };
                    return onClientClick ? (
                      <button
                        key={client}
                        type="button"
                        onClick={() => onClientClick(client)}
                        className={styles.chipClickable}
                      >
                        {cfg.label}
                      </button>
                    ) : (
                      <span key={client} className={styles.chip}>
                        {cfg.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {coworkOnlyFeatures.length > 0 && (
              <div role="note" className={styles.coworkNote}>
                <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.noteIcon}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span>
                  This plugin&rsquo;s <strong>{coworkOnlyFeatureText}</strong> run in Claude Cowork
                  and Claude Code only — not in Claude Chat. Everything else in this plugin runs
                  in Claude Chat too.
                </span>
              </div>
            )}

            {plugin.keywords && plugin.keywords.length > 0 && (
              <div>
                <div className={styles.sectionLabel}>Keywords</div>
                <div className={styles.chipRow}>
                  {plugin.keywords.map((keyword) =>
                    onKeywordClick ? (
                      <button
                        key={keyword}
                        type="button"
                        onClick={() => onKeywordClick(keyword)}
                        className={styles.chipClickable}
                      >
                        {keyword}
                      </button>
                    ) : (
                      <span key={keyword} className={styles.chip}>
                        {keyword}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {plugin.extensionType === "plugin" && plugin.skills && plugin.skills.length > 0 && (
            <div className={styles.skillsSection}>
              <div className={styles.sectionTitle}>
                Skills included · {plugin.skills.length}
              </div>
              <div className={styles.cardList}>
                {plugin.skills.map((skill) => {
                  const linkedEntry = skill.installName ? findPlugin?.(skill.installName) : undefined;
                  // Defend against any skill whose installName resolves back to this same
                  // plugin (e.g. a single skill sharing the plugin's name) — render it
                  // inline rather than as a self-referential link that just re-navigates
                  // to the same page. Catches already-published
                  // catalogs predating the catalog-builder fix that dropped these
                  // installNames (e.g. mythosaur), plus any future regression.
                  const isSelfReference =
                    !!linkedEntry &&
                    !!plugin.installName &&
                    linkedEntry.installName === plugin.installName;
                  // A skill bundled in THIS plugin can only legitimately resolve to an
                  // entry from the same repo. Anything else means the published catalog
                  // resolved the link by bare name and landed in a foreign repo (#547),
                  // where the install snippet and download belong to a different artifact.
                  //
                  // Still required after the processor-side fix (#547), because rebuilds are
                  // webhook-driven: a marketplace.json published by the pre-fix builder keeps
                  // serving until scripts/rebuild-catalog.ts runs. Until then this guard is
                  // the only thing between a user and another repo's page.
                  //
                  // PERMANENT BY DESIGN, not pending removal. Nothing in the payload
                  // distinguishes a catalog built before the fix from one built after
                  // (schemaVersion was deliberately not bumped, #547), so no consumer can
                  // ever prove it is safe to drop. Treating it as temporary is how it gets
                  // deleted during a cleanup pass and the bug returns silently.
                  //
                  // Mirrors the check buildParentIndex in lib/parent-plugin.ts applies to
                  // the breadcrumb (parent) direction; it was missing on the link that
                  // actually navigates.
                  //
                  // Renders inline rather than linking, deliberately: unlinked cards get a
                  // download button (canDownload below) whose proxyKey comes from the
                  // plugin being VIEWED, so it pulls from the correct repo. That is the
                  // whole justification for unlinking, and it is asserted in the tests.
                  const isCrossRepo =
                    !!linkedEntry &&
                    (linkedEntry.source !== plugin.source ||
                      linkedEntry.orgOrOwner !== plugin.orgOrOwner ||
                      linkedEntry.repoName !== plugin.repoName);
                  const isLinked = !!linkedEntry && !isSelfReference && !isCrossRepo;
                  const canDownload = !isLinked && authenticated && !!env.REACT_APP_GITHUB_PROXY_URL;
                  const isDownloading = downloadingSkill === skill.name;

                  async function handleSkillDownload() {
                    setDownloadingSkill(skill.name);
                    setDownloadError(null);
                    try {
                      const url = await getPresignedDownloadUrl(proxyKey, undefined, skill.name);
                      window.location.href = url;
                    } catch (err) {
                      setDownloadError({ skill: skill.name, message: err instanceof Error ? err.message : "Download failed" });
                    } finally {
                      // Guard against clearing a newer download's indicator when two
                      // skill downloads are started in quick succession.
                      setDownloadingSkill((prev) => prev === skill.name ? null : prev);
                    }
                  }

                  const skillIcon = (
                    <div className={`${styles.skillIconTile} ${isLinked ? styles.skillIconTileLinked : ""}`}>
                      <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isLinked ? "var(--color-brand)" : "var(--color-text-muted)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                      </svg>
                    </div>
                  );

                  if (isLinked) {
                    return (
                      <button
                        key={skill.name}
                        type="button"
                        onClick={() => linkedEntry && onNavigateToPlugin?.(linkedEntry)}
                        className={styles.skillCardLinked}
                      >
                        {skillIcon}
                        <div className={styles.cardBody}>
                          <div className={styles.skillNameRow}>
                            <span className={styles.skillNameLinked}>{skill.name}</span>
                            <svg aria-hidden="true" focusable="false" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </div>
                          {skill.description && (
                            <div className={styles.skillDesc}>
                              {skill.description}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  }

                  if (canDownload) {
                    return (
                      <div key={skill.name}>
                        <button
                          type="button"
                          onClick={handleSkillDownload}
                          disabled={isDownloading}
                          aria-label={`Download ${skill.name}`}
                          className={styles.skillDownloadButton}
                        >
                          {skillIcon}
                          <div className={styles.cardBody}>
                            <div className={styles.skillNameRowSplit}>
                              <span className={styles.skillName}>{skill.name}</span>
                              <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.iconShrink}>
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </div>
                            {skill.description && (
                              <div className={styles.skillDesc}>
                                {skill.description}
                              </div>
                            )}
                          </div>
                        </button>
                        {downloadError?.skill === skill.name && (
                          <div className={styles.skillDownloadError}>
                            {downloadError.message}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={skill.name} className={styles.skillCard}>
                      {skillIcon}
                      <div className={styles.cardBody}>
                        <span className={styles.skillName}>{skill.name}</span>
                        {skill.description && (
                          <div className={styles.skillDesc}>
                            {skill.description}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Plugin component sections ───────────────────────── */}
          {/* Per-section accent (icon tile bg + svg stroke) lives in the
              .iconTile* modifier classes; the svg uses stroke="currentColor"
              so both halves of the pair come from one class. */}
          {[
            { key: "mcpServers",   label: "MCP Servers",   tileClass: styles.iconTileBlue,    items: plugin.mcpServers?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "hooks",        label: "Hooks",          tileClass: styles.iconTileAmber,   items: plugin.hooks?.map(s => ({ primary: s.event, secondary: s.description })) },
            { key: "agents",       label: "Agents",         tileClass: styles.iconTilePurple,  items: plugin.agents?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "scripts",      label: "Scripts",        tileClass: styles.iconTileGreen,   items: plugin.scripts?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "monitors",     label: "Monitors",       tileClass: styles.iconTileRed,     items: plugin.monitors?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "outputStyles", label: "Output Styles",  tileClass: styles.iconTileEmerald, items: plugin.outputStyles?.map(s => ({ primary: s.name, secondary: s.description })) },
            { key: "themes",       label: "Themes",         tileClass: styles.iconTileMagenta, items: plugin.themes?.map(s => ({ primary: s.name, secondary: s.description })) },
          ].filter(({ items }) => items && items.length > 0).map(({ key, label, tileClass, items }) => (
            <div key={key} className={styles.section}>
              <div className={styles.sectionTitle}>
                {label} · {items!.length}
              </div>
              <div className={styles.cardList}>
                {items!.map(({ primary, secondary }) => (
                  <div key={primary} className={styles.itemCard}>
                    <div className={`${styles.componentIconTile} ${tileClass}`}>
                      <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
                      </svg>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.itemPrimary}>{primary}</div>
                      {secondary && <div className={styles.itemDesc}>{secondary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {plugin.settings && plugin.settings.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                Settings · {plugin.settings.length}
              </div>
              <table className={styles.settingsTable}>
                <thead>
                  <tr className={styles.settingsHeadRow}>
                    <th className={styles.settingsTh}>Setting</th>
                    <th className={styles.settingsTh}>Description</th>
                    <th className={styles.settingsTh}>Default</th>
                  </tr>
                </thead>
                <tbody>
                  {plugin.settings.map((s) => (
                    <tr key={s.name} className={styles.settingsRow}>
                      <td className={styles.settingsTdName}>{s.name}</td>
                      <td className={styles.settingsTdDesc}>{s.description}</td>
                      <td className={styles.settingsTdDefault}>{s.default ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {plugin.dependencies && plugin.dependencies.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                Dependencies · {plugin.dependencies.length}
              </div>
              <div className={styles.chipWrap}>
                {plugin.dependencies.map((dep) => (
                  <span key={dep} className={styles.depChip}>
                    {dep}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Skill component sections ────────────────────────── */}
          {plugin.triggerPhrases && plugin.triggerPhrases.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Trigger Phrases</div>
              <div className={styles.chipWrap}>
                {plugin.triggerPhrases.map((phrase) => (
                  <span key={phrase} className={styles.triggerChip}>
                    "{phrase}"
                  </span>
                ))}
              </div>
            </div>
          )}

          {plugin.examplePrompts && plugin.examplePrompts.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Example Prompts</div>
              <div className={styles.cardList}>
                {plugin.examplePrompts.map((prompt) => (
                  <div key={prompt} className={styles.promptCard}>
                    <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.cardLeadIcon}>
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <span className={styles.promptText}>{prompt}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plugin.requiredContext && plugin.requiredContext.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Required Context</div>
              <div className={styles.cardListTight}>
                {plugin.requiredContext.map((req) => (
                  <div key={req} className={styles.reqCard}>
                    <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.cardLeadIcon}>
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span className={styles.reqText}>{req}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const resolvedRelated = plugin.relatedSkills
              ?.map((installName) => ({ installName, entry: findPlugin?.(installName) }))
              .filter((p): p is { installName: string; entry: MarketplaceEntry } => !!p.entry);
            if (!resolvedRelated?.length) return null;
            return (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Related Plug-ins and Skills</div>
                <div className={styles.chipWrap}>
                  {resolvedRelated.map(({ installName, entry }) => (
                    <button
                      key={installName}
                      type="button"
                      onClick={() => onNavigateToPlugin?.(entry)}
                      className={styles.relatedChip}
                    >
                      {entry.pluginName} →
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>

        <aside className={styles.aside}>
          <InstallInClaude plugin={plugin} />
          {plugin.extensionType === "plugin" && (
            <details>
              <summary className={styles.cliSummary}>
                CLI install commands
              </summary>
              <InstallInstructions plugin={plugin} />
            </details>
          )}
          <DesktopDownload
            key={installKey}
            plugin={plugin}
          />
          {onHelp && (
            <button onClick={onHelp} className={styles.helpButton}>
              Full setup guide &rarr;
            </button>
          )}
        </aside>

      </div>
    </div>
  );
}
